import { useDisclosure } from '@mantine/hooks';
import { LoadingAnimation, Modal } from '@uag/react-core';
import { useGetAiPlausibilityCheck, useGetRequestDetail } from '@uag/welcome-shared/src/api/queries/requests/requests';
import { CompanyForm } from '@uag/welcome-shared/src/components/company/CompanyForm/CompanyForm';
import { CompanyInformation } from '@uag/welcome-shared/src/components/company/ViewCompany/CompanyInformation';
import { RequestAttachments } from '@uag/welcome-shared/src/components/company/ViewCompany/RequestAttachments';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { useCurrentContributorContext } from 'app/CurrentContributorProvider';
import { ActivateCustomerAction } from './actions/ActivateCustomerAction';
import { ApproveRequestAction } from './actions/ApproveRequestAction';
import { DeclineRequestAction } from './actions/DeclineRequestAction';
import { ForwardRequestAction } from './actions/ForwardRequestAction';
import { checkForDokaResponsibleChange } from './actions/functions';
import { HideRequestAction } from './actions/HideRequestAction';
import { ResendInvitation } from './actions/ResendInvitation';
import { AiPlausibilityHint } from './aiPlausibility/AiPlausibilityHint';
import { ExistingCustomerRequest } from './ExistingCustomerRequest';
import { Remarks } from './Remarks';
import { RequestActions } from './RequestActions';
import { RequesterInformations } from './RequesterInformations';
import { RequestHeader } from './RequestHeader';
import { RequestProvider } from './RequestProvider';

const MAX_AI_PLAUSIBILITY_QUERY_RETRIES = 2;

export const Request = () => {
    const { requestId = '' } = useParams();
    const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
    const { data: aiPlausibilityResponse, error: aiPlausibilityError } = useGetAiPlausibilityCheck(requestId, {
        query: {
            enabled: Boolean(requestId),
            refetchOnMount: 'always',
            retry: (failureCount, error) =>
                (error.response === undefined || error.response.status >= 500)
                && failureCount < MAX_AI_PLAUSIBILITY_QUERY_RETRIES,
        },
    });
    const { data, isLoading } = useGetRequestDetail(requestId);
    const { currentContributor } = useCurrentContributorContext();

    const { t } = useTranslation();
    const request = data?.data;
    const aiPlausibilityResult =
        aiPlausibilityError?.response?.status === 404 ? undefined : aiPlausibilityResponse?.data;

    if (isLoading) {
        return <LoadingAnimation />;
    }
    const backgroundColor =
        request?.currentState === 'DataVerification' ? 'bg-background-white' : 'bg-background-gray-ultra-light';

    const handleEditClick = async () => {
        if (!request || !currentContributor) {
            return;
        }
        if (await checkForDokaResponsibleChange(request, currentContributor)) {
            return;
        }
        openEditModal();
    };

    return (
        <div>
            {request && currentContributor && (
                <div className="gap-md flex flex-col">
                    <RequestProvider request={request}>
                        <RequestHeader>
                            <RequestActions>
                                <HideRequestAction />
                                <ForwardRequestAction visibleInStates={'DataVerification'} />
                                <ActivateCustomerAction visibleInStates={'SignatureVerification'} />
                                <ResendInvitation visibleInStates={'Invited'} />
                                <DeclineRequestAction visibleInStates={'DataVerification'} />
                                <ApproveRequestAction visibleInStates={'DataVerification'} />
                            </RequestActions>
                        </RequestHeader>
                        <div className="gap-base flex flex-col empty:hidden">
                            {aiPlausibilityResult && (
                                <AiPlausibilityHint
                                    canViewDetails={!!currentContributor.isAdmin}
                                    result={aiPlausibilityResult}
                                />
                            )}
                            <ExistingCustomerRequest request={request} />
                        </div>
                        <div className="flex flex-col gap-1">
                            <div className="text-base">{t('companyData')}</div>
                            <div className={clsx('border-border-disabled border text-sm', backgroundColor)}>
                                <CompanyInformation
                                    mode="Backoffice"
                                    request={request}
                                    onEditClick={
                                        request.currentState === 'DataVerification' ? handleEditClick : undefined
                                    }
                                />
                                <Modal
                                    className="w-240"
                                    opened={editModalOpened}
                                    title={t('editCompanyData')}
                                    onClose={closeEditModal}
                                >
                                    <CompanyForm mode="Backoffice" request={request} onClose={closeEditModal} />
                                </Modal>
                            </div>
                        </div>
                        <Remarks />
                        <RequesterInformations request={request} />
                        <RequestAttachments mode="Backoffice" request={request} />
                    </RequestProvider>
                </div>
            )}
        </div>
    );
};
